"use client";
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
  LanguageSelector,
  NavRightSection,
} from "@/components/ui/resizable-navbar";
import { useState, useRef } from "react";
import { motion, useScroll, useMotionValueEvent } from "motion/react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { Globe } from "lucide-react";
import { Language } from "@/data/translations";

interface NewHeaderProps {
  lightBackground?: boolean;
  hideMobileNav?: boolean;
  isBookingPage?: boolean;
}

const NewHeader = ({ 
  lightBackground = false, 
  hideMobileNav = false,
  isBookingPage = false 
}: NewHeaderProps) => {
  const { t, language, setLanguage } = useLanguage();

  const navItems = [
    {
      name: t("nav.rooms"),
      link: "/accommodations",
    },
    {
      name: t("nav.wellness"),
      link: "/spa",
    },
    {
      name: t("nav.dining"),
      link: "/dining",
    },
    {
      name: t("nav.experiences"),
      link: "/local-attractions",
    },
    {
      name: t("nav.activities"),
      link: "/activities",
    },
    {
      name: t("nav.events"),
      link: "/events",
    },
    {
      name: t("nav.journal"),
      link: "/journal",
    },
  ];

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Scroll-based visibility for Nutraswiss banner
  const bannerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll({
    target: bannerRef,
    offset: ["start start", "end start"],
  });
  const [bannerVisible, setBannerVisible] = useState<boolean>(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 100) {
      setBannerVisible(true);
    } else {
      setBannerVisible(false);
    }
  });

  const MenuOverlay = () => (
    <MobileNavMenu
      isOpen={isMobileMenuOpen}
      onClose={() => setIsMobileMenuOpen(false)}
      isBookingPage={isBookingPage}
      data-oid="kfrvw6i"
    >
      {navItems.map((item, idx) => (
        <a
          key={`mobile-link-${idx}`}
          href={getLocalizedUrl(item.link, language)}
          onClick={() => setIsMobileMenuOpen(false)}
          className="relative text-neutral-600 dark:text-neutral-300"
          data-oid="39kk8ei"
        >
          <span className="block" data-oid="zfc37be">
            {item.name}
          </span>
        </a>
      ))}

      {/* Language Selector Section */}
      <div
        className="w-full border-t border-gray-200 pt-4 mt-4"
        data-oid="x5ai.o2"
      >
        <div
          className="flex items-center justify-between mb-3"
          data-oid="qg9eq3r"
        >
          <span
            className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            data-oid="tiggv95"
          >
            {t("nav.language")}
          </span>
          <Globe size={16} className="text-gray-500" data-oid="xmzn0.6" />
        </div>
        <div className="flex space-x-3" data-oid="h4pg_y-">
          {[
            { code: "en", label: "EN", name: "English" },
            { code: "de", label: "DE", name: "Deutsch" },
            { code: "fr", label: "FR", name: "Français" },
          ].map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code as Language);
                setIsMobileMenuOpen(false);
              }}
              className={cn(
                "flex-1 px-4 py-2 rounded-lg text-sm font-lato transition-colors duration-200",
                language === lang.code
                  ? "bg-alpine-gold text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200",
              )}
              data-oid="175z65h"
            >
              <div className="text-center" data-oid="wf_06tq">
                <div className="font-semibold" data-oid=":a-30lf">
                  {lang.label}
                </div>
                <div className="text-xs opacity-75" data-oid="9n61v_8">
                  {lang.name}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </MobileNavMenu>
  );

  return (
    <div className="relative w-full" data-oid="x9yo-z8">
      {/* Nutraswiss Banner */}
      <div
        ref={bannerRef}
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center py-2"
        data-oid="omx.jcm"
      >
        <motion.div
          className={`${bannerVisible ? "bg-white/80 dark:bg-neutral-950/80" : "bg-transparent"}`}
          animate={{
            backdropFilter: bannerVisible ? "blur(10px)" : "none",
            boxShadow: bannerVisible
              ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
              : "none",
            borderRadius: bannerVisible ? "9999px" : "0px",
            paddingLeft: bannerVisible ? "24px" : "16px",
            paddingRight: bannerVisible ? "24px" : "16px",
          }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 50,
          }}
          data-oid="q:ra8he"
        >
          <div
            className="flex items-center justify-between space-x-2 py-1"
                  data-oid="ipsn209"
          >
            <div className="flex items-center space-x-2">
              <span
                className={cn(
                  "text-sm font-medium transition-colors duration-200 whitespace-nowrap",
                  bannerVisible || lightBackground
                    ? "text-gray-900"
                    : "text-white",
                )}
                data-oid="wjpmzyw"
              >
                {t("banner.unitOf")}
              </span>
              <img
                src="/images/NUTRASWISS-LOGO.png"
                alt="Nutraswiss"
                className="h-6 w-auto"
                data-oid="5a3d_7f"
              />
            </div>

            {/* Booking page: show hamburger inside the pill on the far right */}
            {isBookingPage && (
              <div className="ml-auto mr-4 lg:hidden">
                <MobileNavToggle
                  isOpen={isMobileMenuOpen}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  data-oid="booking-banner-toggle"
                />
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <Navbar data-oid="qvum2l3" isBookingPage={isBookingPage}>
        {/* Desktop Navigation */}
        <NavBody 
          data-oid="mq00nsf" 
          lightBackground={lightBackground}
          isBookingPage={isBookingPage}
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMenuOpen={isMobileMenuOpen}
        >
          <NavbarLogo data-oid="cebnl:0" />
          <NavItems
            items={navItems}
            lightBackground={lightBackground}
            data-oid="hdkahxb"
          />

          <NavRightSection data-oid="0du.cao">
            <LanguageSelector
              lightBackground={lightBackground}
              data-oid="msnlilb"
            />
            
            {!isBookingPage && (
              <NavbarButton
                href={import.meta.env.VITE_BOOKING_URL}
                variant="primary"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Header Desktop"
                data-oid="p3rp6.f"
              >
                {t("nav.bookNow")}
              </NavbarButton>
            )}
          </NavRightSection>
        </NavBody>

        {/* Mobile Navigation */}
        {!isBookingPage && (
          <MobileNav
            className={cn(hideMobileNav && "opacity-0 pointer-events-none transition-opacity duration-300")}
            isBookingPage={isBookingPage}
            data-oid="igb_xnz"
          >
            <MobileNavHeader isBookingPage={isBookingPage} data-oid="fnqdt3t">
              <NavbarLogo data-oid="br483vn" />
              <div
                className="absolute left-1/2 transform -translate-x-1/2"
                data-oid="z7gum2d"
              >
                <NavbarButton
                  href={import.meta.env.VITE_BOOKING_URL}
                  variant="primary"
                  className="px-8 py-1 h-12 flex flex-col items-center justify-center shadow-lg border border-white/10 min-w-[140px]"
                  trackingType="booking"
                  trackingLabel={t("nav.bookNow")}
                  trackingLocation="Header Mobile"
                  data-oid="3g:ptyu"
                >
                  <span className="text-sm font-bold uppercase tracking-widest leading-tight">
                    {t("nav.bookNow")}
                  </span>
                  <div className="flex items-center gap-1 opacity-90 mt-0.5">
                    <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-[7px] font-medium uppercase tracking-wide leading-none">
                      Best Rate Guaranteed
                    </span>
                  </div>
                </NavbarButton>
              </div>
              <div className="mr-3">
                <MobileNavToggle
                  isOpen={isMobileMenuOpen}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  data-oid=".y-kflv"
                />
              </div>
            </MobileNavHeader>
          </MobileNav>
        )}

        {/* Menu Overlay shared between Desktop Minimized Pill and Mobile Nav */}
        <MenuOverlay />
      </Navbar>
    </div>
  );
};

export default NewHeader;
